import type { FC, ReactNode } from "react";
import type { PortfolioReportingResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { DetailValueChart } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailValueChart";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { getSummaryValueDate } from "web_myplann/Portfolio/Helpers/getSummaryValueDate";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

type Props = {
	reporting: PortfolioReportingResponseDTO | undefined;
	currencySign: string;
	extraRows?: ReactNode;
	alwaysShowChartInfo?: boolean;
	hideEvaluation?: boolean;
	onEditValue?: () => void;
};

export const DetailValueChartSection: FC<Props> = ({
	reporting,
	currencySign,
	extraRows,
	alwaysShowChartInfo = false,
	hideEvaluation,
	onEditValue,
}) => {
	const { t } = useResource();

	const { daysData, totalValue, totalInvestedValue, showChartInfo } =
		calculateGraphData(
			reporting?.reportingData,
			reporting?.summary ?? undefined,
		);

	const valueDate = getSummaryValueDate(reporting?.summary);

	if (daysData.length === 0) {
		return null;
	}

	return (
		<DetailValueChart
			totalValue={totalValue}
			totalInvestedValue={totalInvestedValue}
			totalEvaluationPercent={reporting?.summary?.totalEvaluationPercent ?? 0}
			daysData={daysData}
			currencySign={currencySign}
			valueDate={valueDate}
			showChartInfo={alwaysShowChartInfo || showChartInfo}
			hideEvaluation={hideEvaluation}
			onEditValue={onEditValue}
			bottomComponent={
				showChartInfo ? (
					<>
						{extraRows}
						<DetailRow
							label={t(Resources.Portfolio.Chart.InvestedTogether)}
							value={moneyFormatter(totalInvestedValue, currencySign, "", 2)}
						/>
						{valueDate && (
							<DetailRow label={t(Resources.Common.ToDate)} value={valueDate} />
						)}
					</>
				) : undefined
			}
		/>
	);
};
